import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { readJson, writeJson } from '../lib/persist';

const AppNavigationContext = createContext(null);
const NAV_STORAGE_KEY = 'vi.activeNav.v1';

export const APP_NAV = {
  OVERVIEW: 'overview',
  TRENDS: 'trends',
  REVENUE_MOVEMENT: 'revenue-movement',
  PRODUCTS: 'products',
  EVENTS: 'events',
  VENUES: 'venues',
  FUNNEL: 'funnel',
  PATHS: 'paths',
  SCAN_HEATMAP: 'scan-heatmap',
  CUSTOMER_SEGMENTS: 'customer-segments',
  REVENUE_PREDICTION: 'revenue-prediction',
  CHAT: 'chat',
  INTEGRATIONS: 'integrations',
  ACCOUNT_SETTINGS: 'account-settings',
  ACCOUNT_MANAGEMENT: 'account-management',
};

export const JOURNEY_NAV_IDS = [APP_NAV.FUNNEL, APP_NAV.PATHS, APP_NAV.SCAN_HEATMAP];

/** Self-contained analytics pages (own filters; no shared FilterBar). */
export const SELF_CONTAINED_NAV_IDS = [
  ...JOURNEY_NAV_IDS,
  APP_NAV.REVENUE_MOVEMENT,
  APP_NAV.CUSTOMER_SEGMENTS,
  APP_NAV.REVENUE_PREDICTION,
];

const VALID_NAV = new Set(Object.values(APP_NAV));

function readActiveNav() {
  const stored = readJson(sessionStorage, NAV_STORAGE_KEY, APP_NAV.OVERVIEW);
  return VALID_NAV.has(stored) ? stored : APP_NAV.OVERVIEW;
}

export function AppNavigationProvider({ children }) {
  const [activeNav, setActiveNav] = useState(readActiveNav);
  const [chatOpen, setChatOpen] = useState(false);
  const [headerTrailing, setHeaderTrailing] = useState(null);

  useEffect(() => {
    writeJson(sessionStorage, NAV_STORAGE_KEY, activeNav);
  }, [activeNav]);

  // Clear page-owned header actions when navigating away.
  useEffect(() => {
    setHeaderTrailing(null);
  }, [activeNav]);

  const value = useMemo(
    () => ({
      activeNav,
      setActiveNav,
      chatOpen,
      setChatOpen,
      headerTrailing,
      setHeaderTrailing,
    }),
    [activeNav, chatOpen, headerTrailing]
  );

  return (
    <AppNavigationContext.Provider value={value}>
      {children}
    </AppNavigationContext.Provider>
  );
}

export function useAppNavigation() {
  const context = useContext(AppNavigationContext);
  if (!context) {
    throw new Error('useAppNavigation must be used within AppNavigationProvider');
  }
  return context;
}
